import React, {useCallback} from 'react';
import {
  View,
  GestureResponderEvent,
  useWindowDimensions,
} from 'react-native';
import {useFolder} from '../../hooks/useFolder';
import {
  EditorLayer,
  EncoderProject,
  Folder,
  FolderType,
} from '../../types';
import {
  Divider,
  IconButton,
  List,
  Portal,
  Text,
} from 'react-native-paper';

import DragList from 'react-native-draglist';
import {TouchableOpacity} from 'react-native-gesture-handler';

import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {getById, getFolderContent, writeItem} from '../../api';
import EditorContentItem from './editorcontentitem';
import {useFileSelectOptions} from '../../hooks/useFileSelectOptions';
import {AttachmentDrawer} from '../contacts/components/attachmentdrawer';

export function EditorLayerHeaderComponent(props) {
  const dataHook = useFolder(
    props.folderId,
    getFolderContent,
    itemId => getById(itemId, props.parentFolderId),
    FolderType.editor,
    writeItem,
  );
  const {width} = useWindowDimensions();
  const isMobile = width < 700;
  const folder = dataHook.folder as EditorLayer;
  const layer = folder?.editor;
  const toggleLayerProp = async (propName: keyof typeof layer) => {
    await writeItem({
      ...dataHook.folder,
      editor: {
        ...(layer || {}),
        [propName]: layer && layer[propName] === true ? false : true,
      },
    } as EditorLayer);
    dataHook.folderContentQuery.refetch();
  };

  const muteLayer = (_e: GestureResponderEvent) =>
    toggleLayerProp('muted');

  const hideLayer = (_e: GestureResponderEvent) =>
    toggleLayerProp('videoDisabled');

  const Controls = (p = {}) => (
    <View style={{flexDirection: 'row', padding: 0}}>
      <IconButton
        {...p}
        size={iconSize}
        icon={layer?.muted ? 'volume-off' : 'volume-high'}
        mode={'outlined'}
        onPress={muteLayer}
      />
      <IconButton
        size={iconSize}
        {...p}
        mode={'outlined'}
        icon={layer?.videoDisabled ? 'video-off' : 'video'}
        onPress={hideLayer}
      />
      <IconButton
        size={iconSize}
        {...p}
        icon={'delete'}
        onPress={async () => {
          await dataHook.onRemoveFolder({
            id: props.folderId,
          } as Folder);
          props.parentHook.folderContentQuery.refetch();
        }}
        mode={'outlined'}
      />
    </View>
  );
  const iconSize = isMobile ? 10 : 14;

  return (
    <View style={{display: 'flex', flexDirection: 'row', height: 68}}>
      <View style={{width: '100%'}}>
        <Divider />
        <List.Item
          title={() =>
            isMobile ? (
              <Controls />
            ) : (
              <TouchableOpacity
                onPressIn={props.onDragStart}
                onPressOut={props.onDragEnd}
                style={{pointerEvents: 'auto'}}>
                <Text style={{fontSize: 14}}>
                  {dataHook.folder?.title}
                </Text>
              </TouchableOpacity>
            )
          }
          right={p => {
            if (isMobile) return null;
            return <Controls />;
          }}
        />
      </View>
    </View>
  );
}

export function EditorLayerBodyComponent(props) {
  const dataHook = useFolder(
    props.folderId,
    getFolderContent,
    itemId => getById(itemId, props.parentFolderId),
    FolderType.editor,
    writeItem,
  );

  const fileSelectOptions = useCallback(
    () => useFileSelectOptions(props.folderId),
    [props.folderId],
  )();
  const renderContent = ({
    item,
    onDragEnd,
    onDragStart,
    index,
  }: {
    item: EncoderProject;
  }) => (
    <EditorContentItem
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      item={item}
      index={index}
      layerIndex={props.layerIndex}
      writeItem={writeItem}
      parentDataHook={dataHook}
    />
  );

  const data = dataHook.folderContent;
  return (
    <View
      style={{
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
      }}>
      <Portal>
        <AttachmentDrawer {...fileSelectOptions} />
      </Portal>
      <View
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
        }}>
        <DragList
          horizontal={true}
          renderItem={renderContent}
          keyExtractor={item => item.id || item.date.toString()}
          data={data || []}
          onReordered={dataHook.onReordered}
          style={{width: '100%'}}
          scrollEnabled={false}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
        />
        <List.Item
          title={'Add Content'}
          titleStyle={{fontSize: 14}}
          style={{pointerEvents: 'auto'}}
          onPress={fileSelectOptions.showDrawer}
          right={props => (
            <>
              <IconButton
                {...props}
                icon={'plus'}
                onPress={fileSelectOptions.showDrawer}
                mode={'outlined'}
              />
            </>
          )}
        />
      </View>
    </View>
  );
}
